// client/src/coms/components/serversettings_parts/RolesManager.js
// Roles tab: role list + grouped permission editor. Permissions, groups, descriptions and what the
// current user may change all come from the server (GET /permissions, GET /servers/:key/roles), which
// enforces the same rules: owner or Manage roles, only roles below your highest role, only permissions
// you hold yourself.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost } from '../../comsApi';

const panel = { padding: 12, borderRadius: 12, border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))', background: 'rgba(0,0,0, calc(0.18 * var(--vx-sh, 1)))', minWidth: 0 };

function errorText(e, catalog) {
  const data = e && e.data;
  const msg = String((data && data.error) || (e && e.message) || 'Something went wrong');
  const keys = data && Array.isArray(data.permissions) ? data.permissions : [];
  if (!keys.length || !catalog) return msg;
  const labels = keys.map((k) => (catalog.byKey.get(k) || {}).label || k);
  return `${msg}: ${labels.join(', ')}`;
}

export function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: 'none',
        padding: 2,
        flexShrink: 0,
        background: checked ? '#3ba55d' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1)))',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 120ms',
      }}
    >
      <span style={{ display: 'block', width: 18, height: 18, borderRadius: 999, background: '#fff', transform: checked ? 'translateX(16px)' : 'translateX(0)', transition: 'transform 120ms' }} />
    </button>
  );
}

export function useCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    comsApiGet('/permissions')
      .then((r) => {
        if (!alive) return;
        const permissions = Array.isArray(r && r.permissions) ? r.permissions : [];
        setCatalog({
          groups: Array.isArray(r && r.groups) ? r.groups : [],
          permissions,
          unavailable: Array.isArray(r && r.unavailable) ? r.unavailable : [],
          byKey: new Map(permissions.map((p) => [p.key, p])),
        });
      })
      .catch(() => alive && setErr('Could not load the permission list.'));
    return () => {
      alive = false;
    };
  }, []);
  return { catalog, err };
}

function ConfirmDialog({ title, children, confirmLabel, onConfirm, onCancel, danger }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0, calc(0.55 * var(--vx-sh, 1)))', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 16 }}>
      <div style={{ ...panel, background: 'var(--vxs-3, #1e1f22)', maxWidth: 440, width: '100%' }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.9 }}>{children}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" className="coms-mini-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="coms-mini-btn" onClick={onConfirm} style={{ background: danger ? '#da373c' : 'var(--vx-accent, #5865f2)', color: 'var(--vx-on-accent, #fff)' }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function draftFromRole(role, catalog) {
  const perms = {};
  for (const p of (catalog && catalog.permissions) || []) perms[p.key] = Number(role[p.key]) === 1;
  return { name: role.name || '', color: role.color || '', position: Number(role.position) || 0, is_default: Number(role.is_default) === 1, perms };
}

export default function RolesManager({ serverKey }) {
  const { catalog, err: catalogErr } = useCatalog();
  const [roles, setRoles] = useState(null);
  const [me, setMe] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [selectedId, setSelectedId] = useState(0);
  const [draft, setDraft] = useState(null);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const r = await comsApiGet(`/servers/${encodeURIComponent(serverKey)}/roles`);
      const list = Array.isArray(r && r.roles) ? r.roles : [];
      setRoles(list);
      setMe(r && r.me ? r.me : null);
      setSelectedId((cur) => (list.some((x) => x.id === cur) ? cur : (list.find((x) => !x.is_owner) || list[0] || {}).id || 0));
    } catch (e) {
      setLoadErr(errorText(e));
    }
  }, [serverKey]);

  useEffect(() => {
    if (serverKey) load();
  }, [serverKey, load]);

  const selected = useMemo(() => (roles || []).find((r) => r.id === selectedId) || null, [roles, selectedId]);

  // Fresh draft whenever another role is selected or the role reloads.
  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft(draftFromRole(selected, catalog));
    setErr('');
  }, [selected, catalog]);

  const grantable = useMemo(() => new Set((me && me.grantable) || []), [me]);
  const canEditRole = !!(selected && selected.can_manage && !selected.is_owner);

  const changes = useMemo(() => {
    if (!draft || !selected || !catalog) return null;
    const perms = {};
    for (const p of catalog.permissions) {
      if (draft.perms[p.key] !== (Number(selected[p.key]) === 1)) perms[p.key] = draft.perms[p.key] ? 1 : 0;
    }
    const meta = {};
    if (draft.name !== (selected.name || '')) meta.name = draft.name;
    if (draft.color !== (selected.color || '')) meta.color = draft.color;
    if (draft.position !== (Number(selected.position) || 0)) meta.position = draft.position;
    if (draft.is_default !== (Number(selected.is_default) === 1)) meta.is_default = draft.is_default ? 1 : 0;
    const count = Object.keys(perms).length + Object.keys(meta).length;
    return count ? { perms, meta, count } : null;
  }, [draft, selected, catalog]);

  async function save(force = false) {
    if (!changes || !selected) return;
    const sensitive = Object.entries(changes.perms)
      .filter(([k, v]) => v === 1 && (catalog.byKey.get(k) || {}).sensitive)
      .map(([k]) => catalog.byKey.get(k).label);
    if (sensitive.length && !force) {
      setConfirm({
        title: 'Grant sensitive permissions?',
        body: (
          <>
            Members with <b>{selected.name}</b> will be able to: <b>{sensitive.join(', ')}</b>. Only grant these to people you trust.
          </>
        ),
        confirmLabel: 'Grant',
        onConfirm: () => {
          setConfirm(null);
          save(true);
        },
      });
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await comsApiPatch(`/roles/${selected.id}`, { ...changes.meta, permissions: changes.perms });
      setNotice('Saved. Members with this role get the change straight away.');
      setTimeout(() => setNotice(''), 3500);
      await load();
    } catch (e) {
      setErr(errorText(e, catalog));
    } finally {
      setBusy(false);
    }
  }

  async function createRole() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setErr('');
    try {
      // New roles go just below the creator's highest role (owners: on top of the others).
      const top = Math.max(0, ...(roles || []).filter((r) => !r.is_owner).map((r) => Number(r.position) || 0));
      const position = me && me.highest_position != null ? Math.max(0, me.highest_position - 1) : top + 1;
      const r = await comsApiPost(`/servers/${encodeURIComponent(serverKey)}/roles`, { name, position });
      setNewName('');
      await load();
      if (r && r.role) setSelectedId(r.role.id);
    } catch (e) {
      setErr(errorText(e, catalog));
    } finally {
      setBusy(false);
    }
  }

  function deleteRole() {
    if (!selected) return;
    setConfirm({
      title: `Delete ${selected.name}?`,
      body: 'Members lose this role and everything it grants. Channel permissions set for it are removed too.',
      confirmLabel: 'Delete role',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setBusy(true);
        try {
          await comsApiDelete(`/roles/${selected.id}`);
          await load();
        } catch (e) {
          setErr(errorText(e, catalog));
        } finally {
          setBusy(false);
        }
      },
    });
  }

  if (catalogErr || loadErr) {
    return (
      <div style={panel}>
        <div className="coms-form-error">{catalogErr || loadErr}</div>
        <button type="button" className="coms-mini-btn" style={{ marginTop: 8 }} onClick={load}>
          Try again
        </button>
      </div>
    );
  }
  if (!catalog || !roles) return <div style={{ ...panel, opacity: 0.7 }}>Loading roles…</div>;

  const q = query.trim().toLowerCase();
  const sortedRoles = [...roles].sort((a, b) => (Number(b.position) || 0) - (Number(a.position) || 0));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ ...panel, flex: '1 1 200px', maxWidth: 280 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Roles</div>
        <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 8 }}>Higher roles outrank lower ones.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sortedRoles.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedId(r.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                textAlign: 'left',
                padding: '7px 9px',
                borderRadius: 8,
                border: 'none',
                background: r.id === selectedId ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.25)' : 'transparent',
                color: 'var(--vx-text, #fff)',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 999, background: r.color || '#99aab5', flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>{r.name}</span>
              {r.is_default ? <span style={{ fontSize: 10, opacity: 0.7 }}>default</span> : null}
              {!r.can_manage ? <span title={r.is_owner ? 'The Owner role always has every permission' : 'Above or equal to your highest role'} aria-label="locked">🔒</span> : null}
            </button>
          ))}
        </div>
        {me && me.can_manage_roles ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New role name" maxLength={64} style={{ flex: 1, minWidth: 0 }} onKeyDown={(e) => e.key === 'Enter' && createRole()} />
            <button type="button" className="coms-mini-btn" disabled={busy || !newName.trim()} onClick={createRole}>
              Create
            </button>
          </div>
        ) : null}
      </div>

      <div style={{ ...panel, flex: '3 1 340px', position: 'relative', paddingBottom: changes ? 64 : 12 }}>
        {!selected || !draft ? (
          <div style={{ opacity: 0.7 }}>Select a role.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={!(selected.can_manage || (selected.is_owner && me && me.is_owner))}
                aria-label="Role name"
                style={{ flex: '1 1 160px', minWidth: 0, fontWeight: 800 }}
              />
              <input type="color" value={/^#[0-9a-f]{6}$/i.test(draft.color) ? draft.color : 'var(--vx-text-2, #99aab5)'} onChange={(e) => setDraft({ ...draft, color: e.target.value })} disabled={!canEditRole} aria-label="Role colour" />
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                Rank
                <input type="number" value={draft.position} onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) || 0 })} disabled={!canEditRole} style={{ width: 64 }} aria-label="Role rank" />
              </label>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} disabled={!canEditRole} />
                Give to new members
              </label>
            </div>

            {selected.is_owner ? (
              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>The Owner role always has every permission and is not affected by channel permissions.</div>
            ) : !selected.can_manage ? (
              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>You can view this role but not change it: it is not below your highest role{me && me.can_manage_roles ? '' : ', or you do not have Manage roles'}.</div>
            ) : null}

            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search permissions" aria-label="Search permissions" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />

            {catalog.groups.map((g) => {
              const items = catalog.permissions.filter((p) => p.group === g.key && (!q || `${p.label} ${p.description}`.toLowerCase().includes(q)));
              if (!items.length) return null;
              const on = items.filter((p) => draft.perms[p.key]).length;
              const isCollapsed = !q && !!collapsed[g.key];
              return (
                <div key={g.key} style={{ borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', padding: '6px 0' }}>
                  <button
                    type="button"
                    onClick={() => setCollapsed({ ...collapsed, [g.key]: !collapsed[g.key] })}
                    aria-expanded={!isCollapsed}
                    style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: 'var(--vx-text, #fff)', cursor: 'pointer', padding: '6px 0', fontWeight: 900, fontSize: 13 }}
                  >
                    <span style={{ display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 120ms' }}>▾</span>
                    <span style={{ flex: 1, textAlign: 'left' }}>{g.label}</span>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>
                      {on}/{items.length}
                    </span>
                  </button>
                  {isCollapsed
                    ? null
                    : items.map((p) => {
                        const lockedReason = selected.is_owner ? 'The Owner role has every permission' : !selected.can_manage ? 'You cannot change this role' : !grantable.has(p.key) ? 'You can only change permissions you have yourself' : '';
                        return (
                          <div key={p.key} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '7px 0 7px 18px' }} title={lockedReason || undefined}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>
                                {p.label}
                                {p.sensitive ? <span className="vx-sensitive-tag" title="Grants power over other members or over the server">Sensitive</span> : null}
                              </div>
                              <div style={{ fontSize: 12, opacity: 0.65, lineHeight: 1.4 }}>{p.description}</div>
                            </div>
                            <Switch checked={selected.is_owner ? true : !!draft.perms[p.key]} disabled={!!lockedReason || busy} label={p.label} onChange={(v) => setDraft({ ...draft, perms: { ...draft.perms, [p.key]: v } })} />
                          </div>
                        );
                      })}
                </div>
              );
            })}

            {catalog.unavailable.length && !q ? (
              <div style={{ borderTop: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', paddingTop: 8, marginTop: 4, fontSize: 12, opacity: 0.55 }}>
                Not available yet: {catalog.unavailable.map((u) => `${u.label} (${u.reason})`).join(' · ')}
              </div>
            ) : null}

            {canEditRole ? (
              <div style={{ marginTop: 12 }}>
                <button type="button" className="coms-mini-btn" onClick={deleteRole} disabled={busy} style={{ color: '#f23f43' }}>
                  Delete role
                </button>
              </div>
            ) : null}

            {err ? <div className="coms-form-error" style={{ marginTop: 10 }}>{err}</div> : null}
            {notice ? <div style={{ marginTop: 10, fontSize: 12, color: '#3ba55d' }}>{notice}</div> : null}

            {changes ? (
              <div style={{ position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'var(--vxs-1, #111214)', boxShadow: '0 4px 16px rgba(0,0,0, calc(0.4 * var(--vx-sh, 1)))' }}>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {changes.count} unsaved change{changes.count === 1 ? '' : 's'}
                </span>
                <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => setDraft(draftFromRole(selected, catalog))}>
                  Reset
                </button>
                <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => save(false)} style={{ background: '#3ba55d', color: '#fff' }}>
                  {busy ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {confirm ? (
        <ConfirmDialog title={confirm.title} confirmLabel={confirm.confirmLabel} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)}>
          {confirm.body}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
